import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@osteopro/ui';
import {
  Building2,
  LayoutDashboard,
  LifeBuoy,
  Plug,
  ScrollText,
  Users,
  type LucideIcon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'wouter';
import { useAuth } from '../../lib/auth-provider.js';
import { isPlatformStaff } from '../../lib/platform-role.js';

/**
 * Groupe « Plateforme » de la barre latérale. Rendu seulement pour qui porte un rôle de
 * plateforme — un praticien n'a rien à faire d'une entrée qui ne mènerait qu'à un refus.
 *
 * Cacher n'est pas interdire : chaque route est **en plus** gardée par `PlatformRoute`, et le
 * serveur refuse de son côté. Ce qui disparaît ici est une porte d'entrée, pas un droit.
 *
 * Une seule liste, ici : une entrée s'ajoute avec son écran, jamais avant. Un lien vers une page
 * qui n'existe pas encore est une promesse que la barre latérale ne peut pas tenir.
 */

interface PlatformEntry {
  /** Clé de traduction sous `platform:nav`, et segment de l'identifiant de test. */
  key: string;
  path: string;
  /** Autres préfixes qui gardent l'entrée allumée : les onglets du même écran. */
  alsoActive?: readonly string[];
  icon: LucideIcon;
  testId: string;
}

const ENTRIES: readonly PlatformEntry[] = [
  {
    key: 'overview',
    path: '/plateforme/apercu',
    icon: LayoutDashboard,
    testId: 'nav-platform-overview',
  },
  {
    key: 'organizations',
    path: '/plateforme/cabinets',
    icon: Building2,
    testId: 'nav-platform-organizations',
  },
  { key: 'users', path: '/plateforme/utilisateurs', icon: Users, testId: 'nav-platform-users' },
  { key: 'tickets', path: '/plateforme/tickets', icon: LifeBuoy, testId: 'nav-platform-tickets' },
  { key: 'services', path: '/plateforme/services', icon: Plug, testId: 'nav-platform-services' },
  // Journal et tâches partagent cette entrée. `nav-jobs` est sur l'onglet Tâches de la page.
  {
    key: 'audit',
    path: '/plateforme/journal',
    alsoActive: ['/plateforme/taches'],
    icon: ScrollText,
    testId: 'nav-platform-audit',
  },
];

export function PlatformNavGroup({ onNavigate }: { onNavigate: (path: string) => void }) {
  const { t } = useTranslation();
  const [location] = useLocation();
  const { user } = useAuth();

  if (!isPlatformStaff(user?.platformRole)) return null;

  return (
    <SidebarGroup>
      <SidebarGroupLabel data-testid="label-sidebar-platform">
        {t('platform:title')}
      </SidebarGroupLabel>
      <SidebarGroupContent>
        <SidebarMenu className="gap-1">
          {ENTRIES.map((entry) => {
            const Icon = entry.icon;
            const label = t(`platform:nav.${entry.key}`);
            const active =
              location.startsWith(entry.path) ||
              (entry.alsoActive?.some((path) => location.startsWith(path)) ?? false);
            return (
              <SidebarMenuItem key={entry.key}>
                <SidebarMenuButton
                  isActive={active}
                  onClick={() => {
                    onNavigate(entry.path);
                  }}
                  tooltip={label}
                  data-testid={entry.testId}
                >
                  <Icon className="h-5 w-5" />
                  <span className="font-medium">{label}</span>
                </SidebarMenuButton>
              </SidebarMenuItem>
            );
          })}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  );
}
